/**
 * Generovanie hesiel pre nové účty zamestnancov.
 *
 * Heslo zakladá admin **niekomu inému** a musí mu ho vedieť odovzdať —
 * server ho už nikdy nevráti (ukladá sa len pbkdf2 hash). Preto sa
 * generuje v prehliadači a hneď sa aj zobrazuje.
 */

/**
 * Abeceda **bez zameniteľných znakov** — heslo sa bude diktovať a
 * prepisovať z papiera, takže vypadli `0`, `O`, `o`, `1`, `l`, `I`, `5`
 * a `S`. Malé `i` a `s` zostávajú: keď v abecede nie sú ich veľké
 * náprotivky ani jednotka s päťkou, nemajú sa s čím pomýliť.
 */
const ABECEDA =
  "abcdefghijkmnpqrstuvwxyzABCDEFGHJKMNPQRTUVWXYZ2346789";

/** Minimum, ktoré vyžaduje backend (`UserCreateRequest.heslo`). */
export const MIN_DLZKA_HESLA = 8;

/**
 * Náhodné heslo zadanej dĺžky.
 *
 * Používa **rejection sampling**, nie `% ABECEDA.length`: 256 nie je
 * násobkom dĺžky abecedy, takže obyčajné modulo by prvých pár znakov
 * vyberalo o niečo častejšie. Pri hesle pre firemný účet to nie je
 * katastrofa, ale odmietnuť pár bajtov navyše nič nestojí.
 */
export function vygenerujHeslo(dlzka = 16): string {
  const n = ABECEDA.length;
  const strop = 256 - (256 % n); // hodnoty >= strop by skreslili výber
  const out: string[] = [];
  const buf = new Uint8Array(dlzka);

  while (out.length < dlzka) {
    crypto.getRandomValues(buf);
    for (const b of buf) {
      if (b >= strop) continue;
      out.push(ABECEDA.charAt(b % n));
      if (out.length === dlzka) break;
    }
  }
  return out.join("");
}

/**
 * Skopíruje text do schránky. Vracia `true`, keď sa to podarilo.
 *
 * Clipboard API je dostupné len v „secure contexte" — appka síce beží na
 * HTTPS, ale dev server na `http://` (a jsdom v testoch) ho nemá vôbec.
 * Preto fallback cez `document.execCommand`, ktorý je síce zastaraný, ale
 * funguje všade a alternatívou je „nedá sa skopírovať".
 */
export async function skopirujDoSchranky(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // padáme na fallback nižšie
  }

  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
